import React, { useState, useEffect, useCallback } from 'react';
import { enseignantsAPI, statutsAPI, importAPI } from '../services/api';
import { getModuleTopbarColor } from './AppSidebar';
import ImportModal from './ImportModal';
import EnseignantsMesVisites from './EnseignantsMesVisites';
import { AvatarEnseignant, ChoixPhotoEnseignant } from './PhotoEnseignant';
import { niveauModule, NIVEAU_MASQUE } from '../services/droits';
import './Enseignants.css';

// ─────────────────────────────────────────────
// Couleurs badge par statut
// ─────────────────────────────────────────────
const STATUT_COLORS = {
    mcf:          { bg: '#dbeafe', text: '#1e40af', label: 'MCF' },
    prag:         { bg: '#dcfce7', text: '#15803d', label: 'PRAG' },
    past:         { bg: '#dcfce7', text: '#15803d', label: 'PAST' },
    ater:         { bg: '#fef9c3', text: '#854d0e', label: 'ATER' },
    doctorant:    { bg: '#f3e8ff', text: '#6b21a8', label: 'Doctorant' },
    vacataire:    { bg: '#f1f5f9', text: '#475569', label: 'Vacataire' },
    charge_cours: { bg: '#fce7f3', text: '#9d174d', label: 'Chargé cours' },
};

function BadgeStatut({ code, libelle }) {
    const s = STATUT_COLORS[code] || { bg: '#f1f5f9', text: '#475569', label: libelle || code };
    return (
        <span className="ens-badge" style={{ background: s.bg, color: s.text }}>
            {s.label}
        </span>
    );
}

// ─────────────────────────────────────────────
// Modale ajout / édition
// ─────────────────────────────────────────────
const FORM_VIDE = {
    nom: '', prenom: '', email: '', telephone: '',
    statut_id: '', heures_service_htd_specifique: '', actif: true
};

function ModalEnseignant({ enseignant, statuts, departementId, anneeId, onSave, onClose, onPhoto }) {
    const [form, setForm] = useState(FORM_VIDE);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState(null);
    // 2026-10-10 (v74) : la photo se dépose TOUT DE SUITE (pas à « Enregistrer ») — elle suit la personne.
    const [photoUrl, setPhotoUrl] = useState(enseignant?.photo_url || null);
    const [photoEnCours, setPhotoEnCours] = useState(false);
    const [photoErreur, setPhotoErreur] = useState(null);
    const photo = async (geste) => {
        setPhotoEnCours(true); setPhotoErreur(null);
        try { const r = await geste(); setPhotoUrl(r.data.photo_url || null); if (onPhoto) onPhoto(); }
        catch (err) { setPhotoErreur(err.response?.data?.error || "La photo n'a pas pu être enregistrée"); }
        finally { setPhotoEnCours(false); }
    };

    useEffect(() => {
        if (enseignant) {
            setForm({
                nom:                          enseignant.nom || '',
                prenom:                       enseignant.prenom || '',
                email:                        enseignant.email || '',
                telephone:                    enseignant.telephone || '',
                statut_id:                    enseignant.statut_id || '',
                heures_service_htd_specifique: enseignant.heures_service_htd_specifique || '',
                actif:                        enseignant.actif !== false,
            });
        } else {
            setForm(FORM_VIDE);
        }
    }, [enseignant]);

    // Heures de service affichées (statut sélectionné ou override)
    const statutSelectionne = statuts.find(s => s.id === parseInt(form.statut_id));
    const heuresService = form.heures_service_htd_specifique
        ? parseFloat(form.heures_service_htd_specifique)
        : statutSelectionne?.heures_service_htd ?? null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nom.trim() || !form.prenom.trim()) {
            setError('Le nom et le prénom sont obligatoires.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...form,
                departement_id: departementId,
                annee_scolaire_id: anneeId,
                statut_id: form.statut_id ? parseInt(form.statut_id) : null,
                heures_service_htd_specifique: form.heures_service_htd_specifique
                    ? parseFloat(form.heures_service_htd_specifique) : null,
            };
            if (enseignant) {
                await enseignantsAPI.update(enseignant.id, payload);
            } else {
                await enseignantsAPI.create(payload);
            }
            onSave();
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ens-modal-overlay" onClick={onClose}>
            <div className="ens-modal" onClick={e => e.stopPropagation()}>
                <div className="ens-modal-header">
                    <h3>{enseignant ? 'Modifier l\'enseignant' : 'Nouvel enseignant'}</h3>
                    <button className="ens-modal-close" onClick={onClose}>
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="ens-modal-body">
                    {error && <div className="ens-alert ens-alert-error">{error}</div>}
                    {enseignant ? (
                        <ChoixPhotoEnseignant e={{ ...enseignant, photo_url: photoUrl }} modifiable enCours={photoEnCours} erreur={photoErreur}
                            onDeposer={(fichier) => photo(() => enseignantsAPI.deposerPhoto(enseignant.id, departementId, fichier))}
                            onRetirer={() => photo(() => enseignantsAPI.retirerPhoto(enseignant.id, departementId).then(() => ({ data: { photo_url: null } })))} />
                    ) : (
                        <div className="ens-photo-aide" style={{ marginBottom: 10 }}><i className="fa-solid fa-camera" /> La photo s'ajoute une fois l'enseignant enregistré (rouvrez sa fiche).</div>
                    )}

                    <div className="ens-form-row">
                        <div className="ens-form-group">
                            <label>Prénom *</label>
                            <input name="prenom" value={form.prenom} onChange={handleChange}
                                placeholder="ex : Jean" required />
                        </div>
                        <div className="ens-form-group">
                            <label>Nom *</label>
                            <input name="nom" value={form.nom} onChange={handleChange}
                                placeholder="ex : DUPONT" required />
                        </div>
                    </div>

                    <div className="ens-form-row">
                        <div className="ens-form-group">
                            <label>Email</label>
                            <input name="email" type="email" value={form.email}
                                onChange={handleChange} placeholder="jean.dupont@univ.fr" />
                        </div>
                        <div className="ens-form-group">
                            <label>Téléphone</label>
                            <input name="telephone" value={form.telephone}
                                onChange={handleChange} placeholder="06 xx xx xx xx" />
                        </div>
                    </div>

                    <div className="ens-form-row">
                        <div className="ens-form-group">
                            <label>Statut</label>
                            <select name="statut_id" value={form.statut_id} onChange={handleChange}>
                                <option value="">— Sélectionner —</option>
                                {statuts.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.libelle} ({s.heures_service_htd}h hTD/an)
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="ens-form-group">
                            <label>
                                Heures service spécifiques
                                <span className="ens-label-hint">(override individuel)</span>
                            </label>
                            <input name="heures_service_htd_specifique" type="number" step="0.5"
                                value={form.heures_service_htd_specifique} onChange={handleChange}
                                placeholder={statutSelectionne ? `Défaut : ${statutSelectionne.heures_service_htd}h` : 'ex : 96'} />
                        </div>
                    </div>

                    {heuresService !== null && (
                        <div className="ens-service-info">
                            <i className="fa-solid fa-circle-info" />
                            Service annuel effectif : <strong>{heuresService} hTD</strong>
                            {form.heures_service_htd_specifique ? ' (override individuel)' : ' (statut par défaut)'}
                        </div>
                    )}

                    {enseignant && (
                        <div className="ens-form-check">
                            <label>
                                <input type="checkbox" name="actif" checked={form.actif} onChange={handleChange} />
                                Enseignant actif
                            </label>
                        </div>
                    )}

                    <div className="ens-modal-actions">
                        <button type="button" className="ens-btn ens-btn-secondary" onClick={onClose}>
                            Annuler
                        </button>
                        <button type="submit" className="ens-btn ens-btn-primary" disabled={saving}>
                            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// Modale générique (statuts, etc.)
// ─────────────────────────────────────────────
function ModalRef({ titre, item, champs, onSave, onClose, saving, error }) {
    const [form, setForm] = useState({});
    useEffect(() => {
        const init = {};
        champs.forEach(c => { init[c.name] = item ? (item[c.name] ?? c.default ?? '') : (c.default ?? ''); });
        setForm(init);
    }, [item]);
    const handleChange = e => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };
    return (
        <div className="ens-modal-overlay" onClick={onClose}>
            <div className="ens-modal" onClick={e => e.stopPropagation()}>
                <div className="ens-modal-header">
                    <h3>{item ? `Modifier — ${titre}` : `Nouveau — ${titre}`}</h3>
                    <button className="ens-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>
                <form className="ens-modal-body" onSubmit={e => { e.preventDefault(); onSave(form); }}>
                    {error && <div className="ens-alert ens-alert-error">{error}</div>}
                    {champs.map(c => (
                        <div className="ens-form-group" key={c.name}>
                            <label>{c.label}{c.required && ' *'}</label>
                            {c.type === 'textarea' ? (
                                <textarea name={c.name} value={form[c.name] || ''} onChange={handleChange}
                                    placeholder={c.placeholder || ''} rows={3} />
                            ) : (
                                <input type={c.type || 'text'} name={c.name} value={form[c.name] ?? ''}
                                    onChange={handleChange} placeholder={c.placeholder || ''} step={c.step} />
                            )}
                        </div>
                    ))}
                    <div className="ens-modal-actions">
                        <button type="button" className="ens-btn ens-btn-secondary" onClick={onClose}>Annuler</button>
                        <button type="submit" className="ens-btn ens-btn-primary" disabled={saving}>
                            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Sauvegarde...</> : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// Onglet Statuts
// ─────────────────────────────────────────────
function OngletStatuts({ statuts, onReload, externalModalOpen, onExternalModalClose }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [itemEdit, setItemEdit] = useState(null);

    // Synchronise l'ouverture depuis le bouton du topbar
    useEffect(() => {
        if (externalModalOpen) {
            setItemEdit(null);
            setModalOpen(true);
            onExternalModalClose();
        }
    }, [externalModalOpen, onExternalModalClose]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const CHAMPS_STATUT = [
        { name: 'code',               label: 'Code',                   required: true, placeholder: 'ex : mcf' },
        { name: 'libelle',            label: 'Libellé',                required: true, placeholder: 'ex : Maître de Conférences' },
        { name: 'heures_service_htd', label: 'Heures service hTD/an',  type: 'number', step: '0.5', default: '192', placeholder: 'ex : 192' },
        { name: 'description',        label: 'Description',            type: 'textarea' },
        { name: 'ordre',              label: "Ordre d'affichage",      type: 'number', default: '0' },
    ];
    const handleSave = async (form) => {
        setSaving(true); setError(null);
        try {
            const payload = {
                code: form.code?.trim(),
                libelle: form.libelle?.trim(),
                heures_service_htd: parseFloat(form.heures_service_htd) || 0,
                description: form.description || null,
                ordre: parseInt(form.ordre) || 0,
            };
            if (itemEdit) { await statutsAPI.update(itemEdit.id, payload); }
            else          { await statutsAPI.create(payload); }
            setModalOpen(false); setItemEdit(null); onReload();
        } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
        finally { setSaving(false); }
    };
    const handleDelete = async (s) => {
        if (!window.confirm(`Supprimer le statut "${s.libelle}" ?`)) return;
        try {
            await statutsAPI.delete(s.id);
            onReload();
        } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    };
    return (
        <div className="ens-content">
            {statuts.length === 0 ? (
                <div className="ens-empty">
                    <i className="fa-solid fa-id-badge" />
                    <p>Aucun statut défini. Cliquez sur "Ajouter un statut" pour commencer.</p>
                </div>
            ) : (
                <div className="ens-table-wrap">
                    <table className="ens-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Libellé</th>
                                <th>Service hTD/an</th>
                                <th>Description</th>
                                <th>Ordre</th>
                                <th className="ens-col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statuts.map(s => (
                                <tr key={s.id}>
                                    <td><BadgeStatut code={s.code} libelle={s.libelle} /></td>
                                    <td>{s.libelle}</td>
                                    <td><span className="ens-heures">{s.heures_service_htd}h</span></td>
                                    <td><span style={{color:'#94a3b8', fontSize:12}}>{s.description || '—'}</span></td>
                                    <td>{s.ordre}</td>
                                    <td className="ens-col-actions">
                                        <button className="ens-action-btn ens-action-edit"
                                            onClick={() => { setItemEdit(s); setModalOpen(true); }}>
                                            <i className="fa-solid fa-pen" />
                                        </button>
                                        <button className="ens-action-btn ens-action-delete"
                                            onClick={() => handleDelete(s)}>
                                            <i className="fa-solid fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="ens-count">{statuts.length} statut(s)</div>
                </div>
            )}
            {modalOpen && (
                <ModalRef
                    titre="Statut enseignant"
                    item={itemEdit}
                    champs={CHAMPS_STATUT}
                    onSave={handleSave}
                    onClose={() => { setModalOpen(false); setItemEdit(null); }}
                    saving={saving}
                    error={error}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────
function Enseignants({ config, anneeId, user }) {
    const [enseignants, setEnseignants] = useState([]);
    const [statuts, setStatuts]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [search, setSearch]           = useState('');
    const [filtreStatut, setFiltreStatut] = useState('');
    const [filtreActif, setFiltreActif]   = useState('actif'); // 'actif' | 'inactif' | 'tous'
    const [modalOpen, setModalOpen]       = useState(false);
    const [enseignantEdit, setEnseignantEdit] = useState(null);
    const [confirmDelete, setConfirmDelete]   = useState(null);
    const [onglet, setOnglet]             = useState('enseignants');
    const [importOpen, setImportOpen]     = useState(false);
    const [statutModalOpen, setStatutModalOpen] = useState(false);
    const [selected, setSelected]         = useState(new Set());

    const deptId  = config?.departement?.id;
    const topbarColor = getModuleTopbarColor('enseignants');
    /**
     * Lot 3.5c-4 (E159 → E161) — « Référentiel · gestion » / « Mon espace » (maquette : la bascule n'apparaît
     * QUE si le compte cumule le droit sur le module ET une fiche enseignant liée). Sans droit : « Mon espace »
     * seul ; sans fiche liée : la gestion seule, comme avant. « Mon espace » ne porte que « Mes visites de stage » (D7).
     */
    const droitGestion = !user || niveauModule(user, deptId, 'enseignants') !== NIVEAU_MASQUE;
    const lie = !!user?.enseignant_id;
    // Spec enseignants.md §1 : le choix de vue est MÉMORISÉ côté client (seulement quand la bascule existe).
    const [vue, setVueEtat] = useState(() => {
        if (!droitGestion) return 'espace';
        try { return lie && localStorage.getItem('enseignants.vue') === 'espace' ? 'espace' : 'gestion'; } catch (_) { return 'gestion'; }
    });
    const setVue = (v) => { setVueEtat(v); try { localStorage.setItem('enseignants.vue', v); } catch (_) { /* mémoire indisponible : sans conséquence */ } };
    const espace = vue === 'espace' && lie;

    const load = useCallback(async () => {
        if (!deptId || !anneeId || !droitGestion) return;
        setLoading(true);
        setError(null);
        try {
            const [resEns, resStat] = await Promise.all([
                enseignantsAPI.getAll(deptId, anneeId),
                enseignantsAPI.getStatuts(),
            ]);
            setEnseignants(resEns.data.enseignants || []);
            setStatuts(resStat.data.statuts || []);
        } catch (err) {
            setError('Impossible de charger les enseignants.');
        } finally {
            setLoading(false);
        }
    }, [deptId, anneeId, droitGestion]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (ens) => {
        try {
            await enseignantsAPI.delete(ens.id, anneeId);
            setConfirmDelete(null);
            load();
        } catch (err) {
            alert(err.response?.data?.error || 'Erreur lors de la suppression');
        }
    };

    // ── Filtrage ──────────────────────────────
    const liste = enseignants.filter(e => {
        const q = search.toLowerCase();
        const matchSearch = !q ||
            e.nom.toLowerCase().includes(q) ||
            e.prenom.toLowerCase().includes(q) ||
            (e.email || '').toLowerCase().includes(q);
        const matchStatut = !filtreStatut || e.statut === filtreStatut || String(e.statut_id) === filtreStatut;
        const matchActif = filtreActif === 'tous' ||
            (filtreActif === 'actif' && e.actif) ||
            (filtreActif === 'inactif' && !e.actif);
        return matchSearch && matchStatut && matchActif;
    });

    const toggleSelect = (id) => setSelected(s => {
        const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
    const allVisibleIds = liste.map(e => e.id);
    const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id));
    const toggleSelectAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(allVisibleIds));
    };
    const handleBulkDelete = async () => {
        const ids = [...selected];
        if (!window.confirm(`Supprimer ${ids.length} enseignant${ids.length > 1 ? 's' : ''} ?`)) return;
        await Promise.all(ids.map(id => enseignantsAPI.delete(id, anneeId)));
        setSelected(new Set());
        load();
    };

    // ── Statistiques rapides ──────────────────
    const stats = statuts.map(s => ({
        ...s,
        count: enseignants.filter(e => e.actif && e.statut_id === s.id).length
    })).filter(s => s.count > 0);

    return (
        <div className="ens-page">

            {/* ── Topbar ── */}
            <div className="ens-topbar" style={{ background: `linear-gradient(135deg, ${topbarColor} 0%, ${topbarColor}cc 100%)` }}>
                <div className="ens-topbar-left">
                    <i className="fa-solid fa-chalkboard-user ens-topbar-icon" />
                    <div>
                        <div className="ens-topbar-title">Enseignants</div>
                        <div className="ens-topbar-sub">
                            {espace ? `Mon espace · ${user?.nom_affiche || ''}`
                                    : `${config?.departement?.nom || 'Département'} · ${enseignants.filter(e => e.actif).length} actif(s)`}
                        </div>
                    </div>
                </div>
                {droitGestion && lie && (
                    <div className="ens-viewsw" title="Visible parce que vous êtes à la fois gestionnaire et enseignant lié à un compte">
                        <button type="button" className={vue === 'gestion' ? 'active' : ''} onClick={() => setVue('gestion')}>
                            <i className="fa-solid fa-table-list" /> <span>Référentiel · gestion</span>
                        </button>
                        <button type="button" className={vue === 'espace' ? 'active' : ''} onClick={() => setVue('espace')}>
                            <i className="fa-solid fa-user" /> <span>Mon espace</span>
                        </button>
                    </div>
                )}
                {espace ? (
                    <div className="ens-topbar-tabs">
                        <button className="ens-topbar-tab active"><i className="fa-solid fa-car-side" /> Mes visites de stage</button>
                    </div>
                ) : (
                <div className="ens-topbar-tabs">
                    <button className={`ens-topbar-tab ${onglet === 'enseignants' ? 'active' : ''}`}
                        onClick={() => setOnglet('enseignants')}>
                        <i className="fa-solid fa-chalkboard-user" /> Enseignants
                    </button>
                    <button className={`ens-topbar-tab ${onglet === 'statuts' ? 'active' : ''}`}
                        onClick={() => setOnglet('statuts')}>
                        <i className="fa-solid fa-id-badge" /> Statuts
                    </button>
                </div>
                )}
                {!espace && (
                <div className="ens-topbar-actions">
                    <button className="ens-btn-import" onClick={() => setImportOpen(true)}>
                        <i className="fa-solid fa-file-import" /> Importer
                    </button>
                    {onglet === 'enseignants' && (
                        <button className="ens-btn-add" onClick={() => { setEnseignantEdit(null); setModalOpen(true); }}>
                            <i className="fa-solid fa-plus" /> Ajouter un enseignant
                        </button>
                    )}
                    {onglet === 'statuts' && (
                        <button className="ens-btn-add" onClick={() => setStatutModalOpen(true)}>
                            <i className="fa-solid fa-plus" /> Ajouter un statut
                        </button>
                    )}
                </div>
                )}
            </div>

            {espace && <EnseignantsMesVisites anneeId={anneeId} user={user} />}

            {!espace && onglet === 'enseignants' && (
                <div className="ens-content">

                    {/* ── Stat cards ── */}
                    {stats.length > 0 && (
                        <div className="ens-stats">
                            {stats.map(s => {
                                const col = STATUT_COLORS[s.code] || { bg: '#f1f5f9', text: '#475569' };
                                return (
                                    <div key={s.id} className="ens-stat-card"
                                        style={{ borderLeftColor: col.text }}
                                        onClick={() => setFiltreStatut(String(s.id))}
                                    >
                                        <div className="ens-stat-count" style={{ color: col.text }}>{s.count}</div>
                                        <div className="ens-stat-label">{s.libelle}</div>
                                    </div>
                                );
                            })}
                            <div className="ens-stat-card" style={{ borderLeftColor: '#94a3b8' }}
                                onClick={() => setFiltreStatut('')}>
                                <div className="ens-stat-count" style={{ color: '#64748b' }}>
                                    {enseignants.filter(e => e.actif).length}
                                </div>
                                <div className="ens-stat-label">Total actifs</div>
                            </div>
                        </div>
                    )}

                    {/* ── Filtres ── */}
                    <div className="ens-toolbar">
                        <div className="ens-search-wrap">
                            <i className="fa-solid fa-magnifying-glass" />
                            <input
                                className="ens-search"
                                placeholder="Rechercher par nom, prénom, email..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {search && (
                                <button className="ens-search-clear" onClick={() => setSearch('')}>
                                    <i className="fa-solid fa-xmark" />
                                </button>
                            )}
                        </div>

                        <select className="ens-filter-select" value={filtreStatut}
                            onChange={e => setFiltreStatut(e.target.value)}>
                            <option value="">Tous les statuts</option>
                            {statuts.map(s => (
                                <option key={s.id} value={String(s.id)}>{s.libelle}</option>
                            ))}
                        </select>

                        <select className="ens-filter-select" value={filtreActif}
                            onChange={e => setFiltreActif(e.target.value)}>
                            <option value="actif">Actifs seulement</option>
                            <option value="inactif">Inactifs seulement</option>
                            <option value="tous">Tous</option>
                        </select>

                        {selected.size > 0 && (
                            <button className="ens-btn ens-btn-danger" onClick={handleBulkDelete}>
                                <i className="fa-solid fa-trash" /> Supprimer la sélection ({selected.size})
                            </button>
                        )}
                    </div>

                    {/* ── Contenu ── */}
                    {loading ? (
                        <div className="ens-center">
                            <i className="fa-solid fa-spinner fa-spin ens-spinner" />
                            <p>Chargement...</p>
                        </div>
                    ) : error ? (
                        <div className="ens-alert ens-alert-error">{error}</div>
                    ) : liste.length === 0 ? (
                        <div className="ens-empty">
                            <i className="fa-solid fa-chalkboard-user" />
                            <p>{search || filtreStatut ? 'Aucun résultat pour ces filtres.' : 'Aucun enseignant enregistré.'}</p>
                            {!search && !filtreStatut && (
                                <button className="ens-btn ens-btn-primary"
                                    onClick={() => { setEnseignantEdit(null); setModalOpen(true); }}>
                                    <i className="fa-solid fa-plus" /> Ajouter le premier enseignant
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="ens-table-wrap">
                            <table className="ens-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 36, textAlign: 'center' }}>
                                            <input type="checkbox"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                title="Tout sélectionner"
                                            />
                                        </th>
                                        <th>Nom</th>
                                        <th>Email</th>
                                        <th>Statut · Service hTD</th>
                                        <th className="ens-col-etat">État</th>
                                        <th className="ens-col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {liste.map(e => {
                                        const heures = e.heures_service_htd_specifique ?? e.statut_heures_htd ?? null;
                                        const statutCode = statuts.find(s => s.id === e.statut_id)?.code;
                                        return (
                                            <tr key={e.id} className={!e.actif ? 'ens-row-inactive' : ''}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input type="checkbox"
                                                        checked={selected.has(e.id)}
                                                        onChange={() => toggleSelect(e.id)}
                                                        onClick={ev => ev.stopPropagation()}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="ens-name">
                                                        <AvatarEnseignant e={e} className="ens-avatar" teinte={false} />
                                                        <div>
                                                            <div className="ens-fullname">{e.prenom} {e.nom}</div>
                                                            {e.telephone && <div className="ens-phone">{e.telephone}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {e.email
                                                        ? <a href={`mailto:${e.email}`} className="ens-email">{e.email}</a>
                                                        : <span className="ens-empty-cell">—</span>
                                                    }
                                                </td>
                                                <td>
                                                    <div className="ens-statut-htd">
                                                        {statutCode
                                                            ? <BadgeStatut code={statutCode} libelle={e.statut_libelle} />
                                                            : <span className="ens-empty-cell">—</span>
                                                        }
                                                        {heures !== null && (
                                                            <span className="ens-heures-badge">{heures}h</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="ens-col-etat">
                                                    <span className={`ens-etat ${e.actif ? 'ens-etat-actif' : 'ens-etat-inactif'}`}>
                                                        {e.actif ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </td>
                                                <td className="ens-col-actions">
                                                    <button className="ens-action-btn ens-action-edit"
                                                        title="Modifier"
                                                        onClick={() => { setEnseignantEdit(e); setModalOpen(true); }}>
                                                        <i className="fa-solid fa-pen" />
                                                    </button>
                                                    <button className="ens-action-btn ens-action-delete"
                                                        title="Supprimer"
                                                        onClick={() => setConfirmDelete(e)}>
                                                        <i className="fa-solid fa-trash" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="ens-count">{liste.length} enseignant(s) affiché(s)</div>
                        </div>
                    )}
                </div>
            )}

            {!espace && onglet === 'statuts' && (
                <OngletStatuts
                    statuts={statuts}
                    onReload={load}
                    externalModalOpen={statutModalOpen}
                    onExternalModalClose={() => setStatutModalOpen(false)}
                />
            )}

            {/* ── Modale ajout/édition ── */}
            {modalOpen && (
                <ModalEnseignant
                    enseignant={enseignantEdit}
                    statuts={statuts}
                    departementId={deptId}
                    anneeId={anneeId}
                    onSave={() => { setModalOpen(false); load(); }}
                    onClose={() => setModalOpen(false)}
                    onPhoto={() => load()}
                />
            )}

            {/* ── Confirm suppression ── */}
            {confirmDelete && (
                <div className="ens-modal-overlay" onClick={() => setConfirmDelete(null)}>
                    <div className="ens-modal ens-modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="ens-modal-header">
                            <h3>Confirmer la suppression</h3>
                            <button className="ens-modal-close" onClick={() => setConfirmDelete(null)}>
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <div className="ens-modal-body">
                            <p>Supprimer <strong>{confirmDelete.prenom} {confirmDelete.nom}</strong> ?</p>
                            <p className="ens-confirm-hint">
                                S'il a des cours affectés, il sera simplement désactivé.
                            </p>
                            <div className="ens-modal-actions">
                                <button className="ens-btn ens-btn-secondary" onClick={() => setConfirmDelete(null)}>
                                    Annuler
                                </button>
                                <button className="ens-btn ens-btn-danger" onClick={() => handleDelete(confirmDelete)}>
                                    <i className="fa-solid fa-trash" /> Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ImportModal ── */}
            {importOpen && (
                <ImportModal
                    titre={onglet === 'enseignants' ? 'Enseignants' : 'Statuts'}
                    champs={onglet === 'enseignants' ? [
                        { key: 'nom',                           label: 'Nom',                    required: true },
                        { key: 'prenom',                        label: 'Prénom',                 required: true },
                        { key: 'email',                         label: 'Email' },
                        { key: 'telephone',                     label: 'Téléphone' },
                        { key: 'statut',                        label: 'Statut (code ou libellé)' },
                        { key: 'heures_service_htd_specifique', label: 'Heures service (override)' },
                    ] : [
                        { key: 'code',               label: 'Code',              required: true },
                        { key: 'libelle',            label: 'Libellé',           required: true },
                        { key: 'heures_service_htd', label: 'Heures service hTD' },
                        { key: 'description',        label: 'Description' },
                    ]}
                    onPreview={async (rows) => {
                        if (onglet === 'enseignants') {
                            const map = {};
                            enseignants.forEach(e => { map[`${e.nom.toLowerCase()}||${e.prenom.toLowerCase()}`] = e; });
                            return rows.map(row => {
                                const key = `${(row.nom||'').toLowerCase().trim()}||${(row.prenom||'').toLowerCase().trim()}`;
                                const existing = map[key];
                                if (!existing) return { status: 'new', row };
                                const changes = [];
                                if (row.email?.trim() && row.email.trim() !== existing.email)
                                    changes.push({ key: 'email', label: 'Email', old: existing.email || '—', new: row.email });
                                if (row.telephone?.trim() && row.telephone.trim() !== existing.telephone)
                                    changes.push({ key: 'telephone', label: 'Téléphone', old: existing.telephone || '—', new: row.telephone });
                                if (row.statut?.trim()) {
                                    const s = statuts.find(s => s.code.toLowerCase() === row.statut.toLowerCase() || s.libelle.toLowerCase() === row.statut.toLowerCase());
                                    if (s && s.id !== existing.statut_id)
                                        changes.push({ key: 'statut', label: 'Statut', old: existing.statut_libelle || '—', new: row.statut });
                                }
                                if (row.heures_service_htd_specifique && parseFloat(row.heures_service_htd_specifique) !== existing.heures_service_htd_specifique)
                                    changes.push({ key: 'heures_service_htd_specifique', label: 'H. service', old: existing.heures_service_htd_specifique ?? '—', new: row.heures_service_htd_specifique });
                                if (changes.length === 0) return { status: 'identical', row, existing };
                                return { status: 'modified', row, existing, changes };
                            });
                        } else {
                            const map = {};
                            statuts.forEach(s => { map[s.code.toLowerCase()] = s; });
                            return rows.map(row => {
                                const existing = map[(row.code||'').toLowerCase().trim()];
                                if (!existing) return { status: 'new', row };
                                const changes = [];
                                if (row.libelle?.trim() && row.libelle.trim() !== existing.libelle)
                                    changes.push({ key: 'libelle', label: 'Libellé', old: existing.libelle, new: row.libelle });
                                if (row.heures_service_htd && parseFloat(row.heures_service_htd) !== existing.heures_service_htd)
                                    changes.push({ key: 'heures_service_htd', label: 'H. service', old: existing.heures_service_htd, new: row.heures_service_htd });
                                if (row.description?.trim() && row.description.trim() !== existing.description)
                                    changes.push({ key: 'description', label: 'Description', old: existing.description || '—', new: row.description });
                                if (changes.length === 0) return { status: 'identical', row, existing };
                                return { status: 'modified', row, existing, changes };
                            });
                        }
                    }}
                    onImport={async (rows) => {
                        const res = await importAPI.importer(
                            onglet === 'enseignants' ? 'enseignants' : 'statuts',
                            deptId, null, rows
                        );
                        load();
                        return res.data;
                    }}
                    onClose={() => setImportOpen(false)}
                />
            )}
        </div>
    );
}

export default Enseignants;
